/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CHATBOT_COMPONENT_NAME,
  CHATBOT_MEMBER_SPECIFIC_COMPONENT,
} from "@/constants";
import type { ComponentDataType } from "@/types";

export const getUniqueId = () => {
  let uniqueId = "";
  uniqueId = Date.now() + (Math.random() * 100).toFixed();
  return uniqueId;
};

export const getCurrentTimestamp = (date: Date): string => {
  const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const weekday = weekdays[date.getDay()];
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  let hour = date.getHours();
  const minute = date.getMinutes();
  const ampm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12 || 12;

  const pad = (n: number) => n.toString().padStart(2, "0");

  return `${weekday}, ${month} ${day}, ${year} ${hour}:${pad(minute)} ${ampm}`;
};

export const getComponentData = (
  memberSpecificResponse: string,
): ComponentDataType => {
  // Match content within double curly braces, including possible comma-separated values
  const regex = /{{([^}]+)}}/g;
  const parametersKey = [
    "componentName",
    "serviceId",
    "networkType",
    "widgetId",
  ];
  let match: RegExpExecArray | null;
  let paramsString: string | null = null;
  while ((match = regex.exec(memberSpecificResponse)) !== null) {
    if (match.index === regex.lastIndex) {
      regex.lastIndex++;
    }
    if (match[1]) {
      paramsString = match[1];
    }
  }
  if (paramsString) {
    const componentTitle = memberSpecificResponse
      .replace(`{{${paramsString}}}`, "")
      .trim();
    const params = paramsString.split(",").map((p) => p.trim());
    const paramsObj: Record<string, string> = {};
    params.forEach((param, index) => {
      if (parametersKey[index]) {
        paramsObj[parametersKey[index]] = param;
      }
    });
    return {
      componentTitle,
      componentName: paramsObj.componentName,
      serviceId: paramsObj.serviceId,
      networkType: paramsObj.networkType,
      widgetId: paramsObj.widgetId,
    };
  }
  return null;
};

export const hasAtLeastOneComponentAsKey = (obj: {
  [key: string]: unknown;
}) => {
  return CHATBOT_MEMBER_SPECIFIC_COMPONENT.some((key) =>
    Object.prototype.hasOwnProperty.call(obj, key),
  );
};

export const getMatchingComponentKey = (obj: { [key: string]: unknown }) => {
  return CHATBOT_MEMBER_SPECIFIC_COMPONENT.find((key) =>
    Object.prototype.hasOwnProperty.call(obj, key),
  );
};

export const isValidProviderDetails = (obj: any) =>
  Array.isArray(obj?.widget) &&
  obj?.[CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS] &&
  [
    "website",
    "phone",
    "display_email",
    "service_name",
    "service_provider_email",
    "service_id",
    "service_provider",
    "display_website",
    "related_queries",
  ].every((key) => key in obj[CHATBOT_COMPONENT_NAME.PROVIDER_DETAILS]);

export const isValidBenefitDocument = (obj: any) =>
  Array.isArray(obj?.widget) &&
  obj?.[CHATBOT_COMPONENT_NAME.BENEFIT_DOCUMENT] &&
  ["document_link"].some(
    (key) => key in obj[CHATBOT_COMPONENT_NAME.BENEFIT_DOCUMENT],
  );

export const isValidMemberScope = (obj: any) =>
  Array.isArray(obj?.widget) &&
  obj?.[CHATBOT_COMPONENT_NAME.MEMBER_SCOPE] &&
  ["content"].some((key) => key in obj[CHATBOT_COMPONENT_NAME.MEMBER_SCOPE]);

export const isValidMemberMedication = (obj: any) =>
  Array.isArray(obj?.widget) &&
  obj?.[CHATBOT_COMPONENT_NAME.MEMBER_MEDICATION] &&
  ["content"].some((key) => key in obj);
